import React, { useContext } from "react"
import { Button, Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faArrowsRotate,
	faBullseye,
	faChevronRight,
	faClipboardList,
	faComment,
} from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import CardInset from "./CardInset"
import ContentPill from "../atoms/ContentPill"
import { modalContext } from "../providers/modal.provider"
import { notebookContext } from "../providers/notebook.provider"
import { SchemaPreview } from "./schema/ExerciseSchema"
import { sharedDataContext } from "../providers/sharedData.provider"
import { getLessonColor } from "../utils/func.utils"
import { isCancelledLesson } from "../utils/cancellation.utils"
import { isMatch } from "../utils/match.utils"
import {
	hasLessonNotes,
	isLessonContentEmpty,
	isProgramEmpty,
	joinPhaseLabels,
	chosenAreas,
	normalizeLessonContent,
	notePlayers,
	playerStyleLabel,
	splitPhases,
} from "../utils/lessonContent.utils"
import { notebookUpdates } from "../utils/notebook.utils"

// Com'è andata, nel riquadro: il diario e le righe sugli allievi, col nome
// di battesimo di ognuno
function LessonNotes({ lessonData, content, isFirst }) {
	const { getLessonType } = useContext(sharedDataContext)
	const nameColor = getLessonColor(getLessonType(lessonData.lessonType), true)
	// tutti quelli della lezione, anche se oggi erano assenti: la riga resta sua
	const names = Object.fromEntries(
		notePlayers(
			(lessonData.partecipants ?? []).map(player => ({ ...player, isPresent: true })),
		).map(player => [player.playerID, player.label]),
	)
	const playerNotes = Object.entries(content.playerNotes ?? {}).filter(([id]) => names[id])
	const noteColor = theme.colors.gunmetal

	return (
		// sotto al programma col filo; da sole, senza
		<Div
			p={{ t: isFirst ? "0" : "0.5rem" }}
			border={isFirst ? undefined : { t: "1px solid" }}
			borderColor={theme.colors.surface}>
			<Text textSize="caption" textWeight="bold" textColor={noteColor}>
				Com'è andata:
			</Text>
			{content.diary && (
				<Text textSize="caption" style={{ whiteSpace: "pre-line", lineHeight: 1.45 }}>
					{content.diary}
				</Text>
			)}
			{playerNotes.length > 0 && (
				<Div
					m={{ t: content.diary ? "0.375rem" : "0" }}
					style={{
						display: "grid",
						gridTemplateColumns: "auto minmax(0, 1fr)",
						gap: "0.125rem 0.375rem",
						// come il diario sopra
						fontSize: "12px",
						lineHeight: 1.45,
					}}>
					{playerNotes.map(([playerID, text]) => (
						<React.Fragment key={playerID}>
							<b style={{ color: nameColor }}>{names[playerID]}:</b>
							<span>{text}</span>
						</React.Fragment>
					))}
				</Div>
			)}
		</Div>
	)
}

// "Un esercizio è cambiato nel quaderno", "2 esercizi sono cambiati…"
const updatesLabel = n =>
	n === 1 ? "Un esercizio è cambiato nel quaderno" : `${n} esercizi sono cambiati nel quaderno`

// Il riquadro "Programma" della lezione aperta: cosa si fa o si è fatto in
// campo (src/utils/lessonContent.utils.js). Toccato, apre il foglio. Chi
// la lezione non la può modificare il contenuto lo legge, se c'è; una
// lezione annullata non si è fatta, e un contenuto nuovo non lo prende. Un
// esercizio con lo schema ce l'ha accanto in miniatura, e toccata si apre
// grande.
//
// Un esercizio preso dal quaderno che lì è andato avanti (gli si è
// disegnato lo schema, se n'è corretto il testo) lo dice in fondo al
// programma, a chi la lezione la può cambiare: la riga apre il foglio, dove
// lo si riprende con **Aggiorna**. Da sola non cambia niente: la lezione
// resta com'è stata fatta finché non lo si sceglie.
function LessonContent({ lessonData, canEdit }) {
	const Modal = useContext(modalContext)
	const { getEntry } = useContext(notebookContext) ?? {}

	// quando giochi tu non c'è una lezione da preparare
	if (isMatch(lessonData)) return null

	const isEmpty = isLessonContentEmpty(lessonData.lessonContent)
	const canAdd = canEdit && !isCancelledLesson(lessonData)
	if (isEmpty && !canAdd) return null

	const open = () => Modal.open("lessonContent", lessonData)
	// a lezione finita, com'è andata: il foglio delle note veloci
	const isOver = lessonData.lessonEnd?.toDate?.() < new Date()
	const openNotes = () => Modal.open("lessonDiary", lessonData)

	// Senza programma il riquadro c'è lo stesso, nel posto dove starà, e fa
	// una domanda: prima della lezione chiede il piano, a lezione finita il
	// diario. Pieno e senza tratteggio, che nella scheda vuol dire "manca
	// qualcuno" (il sostituto).
	if (isEmpty) {
		const prompt = isOver
			? {
					icon: faComment,
					title: "Com'è andata?",
					hint: "Due righe, e su qualcuno in particolare",
					action: "Racconta",
				}
			: {
					icon: faClipboardList,
					title: "Cosa facciamo?",
					hint: "Il piano: obiettivo ed esercizi",
					action: "Scrivi",
				}
		return (
			<CardInset>
				<Button
					m={{ t: "0.75rem" }}
					w="100%"
					h="auto"
					p={{ l: "0.875rem", r: "1rem", y: "0.75rem" }}
					bg="background"
					rounded="15px"
					d="flex"
					justify="flex-start"
					align="center"
					style={{ gap: "0.75rem", textAlign: "left" }}
					aria-label={`${prompt.title} ${prompt.action}`}
					onClick={isOver ? openNotes : open}>
					<span
						aria-hidden="true"
						style={{
							flex: "none",
							width: "2.25rem",
							height: "2.25rem",
							borderRadius: "50%",
							background: theme.colors.gunmetal_lighter,
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
						}}>
						<FontAwesomeIcon icon={prompt.icon} color={theme.colors.gunmetal} />
					</span>
					<Div flexGrow="1" style={{ minWidth: 0 }}>
						<Text textWeight="bold" textColor={theme.colors.text}>
							{prompt.title}
						</Text>
						<Text textSize="caption" textColor={theme.colors.text_light}>
							{prompt.hint}
						</Text>
					</Div>
					<Text tag="span" textWeight="600" textColor={theme.colors.primary}>
						{prompt.action}
					</Text>
				</Button>
			</CardInset>
		)
	}

	const content = normalizeLessonContent(lessonData.lessonContent)
	const { written, missing } = splitPhases(content)
	const updates = canAdd && getEntry ? notebookUpdates(content, getEntry).length : 0
	const hasNotes = hasLessonNotes(content)
	const pills = [
		...(content.playerStyle
			? [
					{
						key: content.playerStyle,
						text: playerStyleLabel(content.playerStyle),
						kind: "playerStyle",
					},
				]
			: []),
		// per macroaree, col dettaglio solo se non sono tutte le voci
		...chosenAreas(content).map(({ area, detail }) => ({
			key: area.id,
			text: area.label,
			detail,
			kind: area.list === "situations" ? "situation" : "stroke",
		})),
	]

	// Il riquadro si tocca tutto, come quando è vuoto: un "Modifica" qui
	// faceva tre con quello delle note e quello della lezione. Prima della
	// lezione apre il programma, dopo "Com'è andata?", che da lì apre il
	// programma intero. La freccia dice che si apre, ed è il tasto vero per
	// chi non tocca: il riquadro intero come tasto si leggerebbe tutto d'un
	// fiato, e non si leggerebbe più quello che c'è scritto.
	const edit = isOver ? openNotes : open
	const editLabel = !isOver
		? "Modifica il programma"
		: hasNotes
			? "Modifica com'è andata"
			: "Com'è andata? Racconta"

	return (
		<CardInset>
			<Div
				m={{ t: "0.75rem" }}
				p={{ x: "1rem", y: "0.75rem" }}
				// dentro la scheda della lezione, che può essere bianca: il fondo caldo
				bg="background"
				rounded="15px"
				d="flex"
				flexDir="column"
				cursor={canAdd ? "pointer" : undefined}
				style={{ gap: "0.5rem" }}
				onClick={canAdd ? edit : undefined}>
				<Div d="flex" justify="space-between" align="center">
					<Text textSize="subheader" textWeight="bold">
						Programma:
					</Text>
					{canAdd && (
						<Button
							h="2rem"
							w="2rem"
							p="0"
							m={{ r: "-0.5rem" }}
							bg="transparent"
							aria-label={editLabel}
							onClick={event => {
								// il riquadro sotto lo aprirebbe un'altra volta
								event.stopPropagation()
								edit()
							}}>
							<FontAwesomeIcon icon={faChevronRight} color={theme.colors.text_light} />
						</Button>
					)}
				</Div>
				{content.fromNotebook && (
					<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "-0.375rem" }}>
						Dal quaderno: «{content.fromNotebook.title}»
					</Text>
				)}
				{content.objective && (
					<Div
						d="flex"
						align="baseline"
						p="0.5rem 0.75rem"
						bg={theme.colors.gunmetal_lighter}
						rounded="15px"
						style={{ gap: "0.5rem" }}>
						<FontAwesomeIcon icon={faBullseye} color={theme.colors.gunmetal} size="sm" />
						<Text textSize="caption" textColor={theme.colors.gunmetal}>
							<b>Obiettivo:</b> {content.objective}
						</Text>
					</Div>
				)}
				{pills.length > 0 && (
					<Div d="flex" flexWrap="wrap" style={{ gap: "0.375rem" }}>
						{pills.map(({ key, text, detail, kind }) => (
							<ContentPill key={key} kind={kind} detail={detail}>
								{text}
							</ContentPill>
						))}
					</Div>
				)}
				{written.length > 0 && (
					<Div>
						{written.map(phase => {
							const { objective, exercises, schemas } = content.phases[phase.id]
							return (
								<Div
									key={phase.id}
									p={{ y: "0.5rem" }}
									border={{ t: "1px solid" }}
									borderColor={theme.colors.surface}>
									<Text
										textSize="caption"
										textWeight="bold"
										textColor={theme.colors.gunmetal}>
										{phase.label}
									</Text>
									{objective && (
										<Div d="flex" align="baseline" style={{ gap: "0.375rem" }}>
											<FontAwesomeIcon
												icon={faBullseye}
												color={theme.colors.gunmetal}
												size="xs"
											/>
											<Text
												textSize="caption"
												textColor={theme.colors.gunmetal}
												style={{ fontStyle: "italic" }}>
												{objective}
											</Text>
										</Div>
									)}
									{exercises.length > 0 && (
										<ul
											style={{
												margin: "0.125rem 0 0",
												paddingLeft: "1.125rem",
												fontSize: "0.8125rem",
												lineHeight: 1.4,
											}}>
											{exercises.map((exercise, i) => (
												<li key={i}>
													{schemas?.[i] ? (
														<span
															style={{
																display: "flex",
																alignItems: "flex-start",
																gap: "0.625rem",
															}}>
															<span style={{ flex: 1, minWidth: 0 }}>{exercise}</span>
															<SchemaPreview
																schema={schemas[i]}
																label={exercise || `${phase.label}, esercizio ${i + 1}`}
															/>
														</span>
													) : (
														exercise
													)}
												</li>
											))}
										</ul>
									)}
								</Div>
							)
						})}
						{/* quelle ancora vuote in una riga sola, non una per fase */}
						{missing.length > 0 && (
							<Text
								p={{ t: "0.5rem" }}
								border={{ t: "1px solid" }}
								borderColor={theme.colors.surface}
								textSize="caption"
								textColor={theme.colors.text_light}
								style={{ fontStyle: "italic" }}>
								{joinPhaseLabels(missing)}: da scrivere
							</Text>
						)}
					</Div>
				)}
				{updates > 0 && (
					<Button
						w="100%"
						h="auto"
						p={{ t: "0.5rem", x: "0" }}
						bg="transparent"
						rounded="0"
						border={{ t: "1px solid" }}
						borderColor={theme.colors.surface}
						d="flex"
						justify="space-between"
						align="center"
						style={{ gap: "0.5rem", textAlign: "left" }}
						aria-label={`${updatesLabel(updates)}: guarda`}
						onClick={event => {
							// il riquadro sotto aprirebbe, a lezione finita, le note
							event.stopPropagation()
							open()
						}}>
						<Text
							tag="span"
							textSize="caption"
							textWeight="bold"
							textColor={theme.colors.gunmetal}
							d="flex"
							align="center"
							style={{ gap: "0.375rem" }}>
							<FontAwesomeIcon icon={faArrowsRotate} size="sm" />
							{updatesLabel(updates)}
						</Text>
						<Text tag="span" textSize="caption" textWeight="600" textColor={theme.colors.primary}>
							Guarda
						</Text>
					</Button>
				)}
				{hasNotes && (
					<LessonNotes
						lessonData={lessonData}
						content={content}
						isFirst={isProgramEmpty(content)}
					/>
				)}
				{/* finita, col programma ma senza note: com'è andata si chiede qui,
				    e il riquadro toccato apre quello */}
				{!hasNotes && isOver && canAdd && (
					<Div
						p={{ t: "0.5rem" }}
						border={{ t: "1px solid" }}
						borderColor={theme.colors.surface}
						d="flex"
						justify="space-between"
						align="center"
						aria-hidden="true">
						<Text textSize="caption" textWeight="bold" textColor={theme.colors.gunmetal}>
							Com'è andata?
						</Text>
						<Text
							tag="span"
							textSize="caption"
							textWeight="600"
							textColor={theme.colors.primary}>
							Racconta
						</Text>
					</Div>
				)}
			</Div>
		</CardInset>
	)
}

export default LessonContent
